import { Option } from 'lib.context/src/LandingEditorContext';
import { OptionIds } from 'lib.context/src/constants/constantslp';
import { ButtonType } from 'lib.context/src/models/landingPage';
import { AdFormats } from '..';
import { CreativeAdItem } from 'lib.store/src/type-partner/creatives/models';
import { CardType } from 'lib.context/src/models/landingPage';

const updateElementHtml = (element: HTMLElement, html: string) => {
  try {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }

    Array.from(wrapper.childNodes).forEach((node) => {
      if (node.nodeName.toLowerCase() === 'script') {
        const originalScript = node as HTMLScriptElement;
        const script = document.createElement('script');
        if (originalScript.type) {
          script.type = originalScript.type;
        }
        Array.from(originalScript.attributes).forEach((attr) => {
          script.setAttribute(attr.name, attr.value);
        });
        script.text = originalScript.text || '';
        element.appendChild(script);
      } else {
        element.appendChild(node.cloneNode(true));
      }
    });
  } catch (error) {
    console.error('Failed to update HTML embed', error);
  }
};

// localStorage key constant
export const LANDING_EDITOR_PREV_URL_KEY = 'url_before_landing_editor';

// Valid URL patterns for returning from Landing Editor
const VALID_RETURN_PATTERNS = [
  '/events/edit/',
  '/campaigns/edit/',
  '/campaigns/create/',
  '/campaigns/',
  '/events',
  '/campaigns',
  '/landing-pages',
];

/**
 * Checks if stored URL is valid for navigation back
 */
const isValidReturnUrl = (url: string | null, currentPathname: string): boolean => {
  if (!url) return false;

  // URL must not match the current path
  if (url === currentPathname || currentPathname.startsWith(url + '/landing')) return false;

  // URL must match one of the valid patterns
  return VALID_RETURN_PATTERNS.some((pattern) => url.includes(pattern));
};

/**
 * Determines fallback URL based on current pathname
 */
const getFallbackUrl = (pathname: string): string | null => {
  // Pattern: /events/edit/{eventId}/landing/...
  const eventsMatch = pathname.match(/^(\/events\/edit\/\d+)/);
  if (eventsMatch) {
    return `${eventsMatch[1]}/campaigns-channel`;
  }

  // Pattern: /campaigns/{campaignId}/... (new simplified URL)
  const newCampaignMatch = pathname.match(/^\/campaigns\/(\d+)/);
  if (newCampaignMatch) {
    return `/campaigns/${newCampaignMatch[1]}`;
  }

  // Pattern: /campaigns/edit/{eventId}/campaign/{campaignId}/landing/... (legacy)
  const campaignsMatch = pathname.match(/^(\/campaigns\/edit\/[^/]+\/campaign\/[^/]+)/);
  if (campaignsMatch) {
    return campaignsMatch[1];
  }

  // Pattern: /campaigns/create/new/{eventId}/campaign/{campaignId}/landing/... (legacy)
  const campaignsCreateMatch = pathname.match(
    /^(\/campaigns\/create\/new\/[^/]+\/campaign\/[^/]+)/,
  );
  if (campaignsCreateMatch) {
    return campaignsCreateMatch[1];
  }

  // Pattern: /landing-pages/... (new simplified URL)
  if (pathname.startsWith('/landing-pages/')) {
    return '/landing-pages';
  }

  return null;
};

/**
 * Clears stored URL from localStorage
 */
export const clearLandingEditorPrevUrl = () => {
  localStorage.removeItem(LANDING_EDITOR_PREV_URL_KEY);
};

/**
 * Saves URL before navigating to Landing Editor
 */
export const setLandingEditorPrevUrl = (url: string) => {
  localStorage.setItem(LANDING_EDITOR_PREV_URL_KEY, url);
};

/**
 * Returns a validated return URL for the landing editor breadcrumbs.
 * Checks stored URL in localStorage, validates it, falls back to pathname-based URL.
 */
export const getLandingEditorReturnUrl = (pathname: string): string | null => {
  const storedUrl = localStorage.getItem(LANDING_EDITOR_PREV_URL_KEY);
  const isStoredUrlOk = isValidReturnUrl(storedUrl, pathname);

  if (isStoredUrlOk && storedUrl) {
    return storedUrl;
  }

  // Clear invalid stored URL
  if (storedUrl && !isStoredUrlOk) {
    clearLandingEditorPrevUrl();
  }

  return getFallbackUrl(pathname);
};

export const changeVideoUrl = (url?: string) => {
  if (!url) return url;

  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.hostname.includes('youtube.com')) {
      let videoId = parsedUrl.searchParams.get('v');

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      } else if (parsedUrl.pathname.includes('/embed/')) {
        const cleanUrl = `https://www.youtube.com${parsedUrl.pathname}`;
        return cleanUrl;
      }
    }

    return url;
  } catch (e) {
    return url;
  }
};

export const convertPercentToDecimal = (rgbString: string): string => {
  return rgbString.replace(/(\d+)%/g, (_, percent) => {
    const decimal = parseInt(percent, 10) / 100;
    return decimal.toString();
  });
};

export const setCursorToEnd = (element: HTMLElement) => {
  const range = document.createRange();
  const selection = window.getSelection();

  range.selectNodeContents(element);
  range.collapse(false);

  selection?.removeAllRanges();
  selection?.addRange(range);
};

const socialSuffixes = [
  'facebook',
  'instagram',
  'linkedin',
  'telegram',
  'twitter',
  'viber',
  'whatsapp',
];

export const setNewEditorStyles = ({
  clickedSectionId,
  option,
}: {
  clickedSectionId: string | null | undefined;
  option: Option;
}) => {
  if (!clickedSectionId) {
    return;
  }
  const editor = document.querySelector(`[data-editor-id="${clickedSectionId}"]`);
  if (option.name === OptionIds.CardsComponent) {
    const imageElements = editor?.querySelectorAll(`img[class*="${option.name}-"]`) || [];
    imageElements.forEach((imageElement, index) => {
      // @ts-ignore
      const imageElementValue = option.value?.card?.[index];
      (imageElement as HTMLImageElement).src = imageElementValue?.image || '';
      (imageElement as HTMLImageElement).style.borderRadius = imageElementValue?.cornerRadius;
    });

    // @ts-ignore
    option.value?.card?.forEach((card, index) => {
      // @ts-ignore
      const cardElementValue = option.value?.card?.[index]?.socialLinks;
      let socialLinks: NodeListOf<Element> | [] | Element[] = [];
      socialSuffixes.forEach((suffix) => {
        const foundLinks =
          editor?.querySelectorAll(`[class*="${option.name}-${index}"][class*="-${suffix}"]`) || [];
        socialLinks = [...socialLinks, ...Array.from(foundLinks)];
      });
      socialLinks.forEach((link) => {
        const className = link.className;
        const socialType = Object.keys(cardElementValue).find((type) =>
          className.includes(`-${type}`),
        );
        if (socialType && cardElementValue[socialType] && cardElementValue[socialType] !== 'null') {
          (link as HTMLElement).classList.add('social-link-visible-class');
        } else {
          (link as HTMLElement).classList.add('social-link-hidden-class');
        }
      });
    });
    return;
  }

  let optionName = option.name;
  if (optionName === OptionIds.ButtonsComponent) {
    optionName = OptionIds.ButtonComponent;
  }
  const elements =
    editor?.querySelectorAll(
      `[class*="${optionName}-"]:not([class$="-wrapper"]):not([class$="-overlay"])`,
    ) || [];
  elements.forEach((element, index) => {
    let newValue = typeof option.value === 'string' ? option.value : '';
    if (Array.isArray(option.value)) {
      const arrayValue = option.value[index];
      if (typeof arrayValue === 'string') {
        newValue = arrayValue;
      } else {
        newValue = (arrayValue as Record<string, string>)?.[option.key || ''];
      }
    }

    if (option.name === OptionIds.ButtonsComponent) {
      const buttonValue = (option.value as { button: ButtonType[] })?.button?.[
        index
      ] as any as ButtonType;
      newValue = buttonValue?.[(option.key || '') as keyof ButtonType] as string;
    }

    switch (option.key) {
      case 'imageOpacity':
      case 'opacity': {
        const nextElementSibling = element.nextElementSibling as HTMLElement;
        if (nextElementSibling) {
          nextElementSibling.style.opacity = newValue;
        }
        if (option.key !== 'imageOpacity') {
          (element as HTMLElement).style.opacity = newValue;
        }
        break;
      }
      case 'backgroundColor': {
        (element as HTMLElement).style.backgroundColor = newValue;
        if (option.name === OptionIds.BackgroundComponent) {
          (element as HTMLElement).style.backgroundImage = 'none';
        }
        break;
      }
      case 'baseColor': {
        (element as HTMLElement).style.setProperty('--custom-base-color', newValue);
        (element as HTMLElement).classList.add('editor-base-color-class');
        break;
      }
      case 'backgroundImage': {
        (element as HTMLElement).style.backgroundImage = `url('${newValue}')`;
        if (option.name === OptionIds.BackgroundComponent) {
          (element as HTMLElement).style.backgroundColor = '';
        }
        break;
      }
      case 'backgroundSize': {
        (element as HTMLElement).style.backgroundSize =
          typeof newValue === 'string' ? newValue : `${newValue}`;
        break;
      }
      case 'textColor': {
        if (optionName === 'TextComponent') {
          (element as HTMLElement).style.setProperty('--custom-text-color', newValue);
          (element as HTMLElement).classList.add('editor-text-color-class');
        } else {
          (element as HTMLElement).style.setProperty('--custom-button-color', newValue);
          (element as HTMLElement).classList.add('editor-button-color-class');
        }
        break;
      }
      case 'strokeColor': {
        (element as HTMLElement).style.border = `1px solid ${newValue}`;
        break;
      }
      // todo ImageComponent
      case 'image': {
        (element as HTMLImageElement).src = newValue || '';
        break;
      }
      case 'video': {
        const fixedVideoUrl = changeVideoUrl(newValue);
        const iframe = element?.querySelector('iframe');
        if (iframe) {
          iframe.src = fixedVideoUrl || '';
        }
        break;
      }
      case 'cornerRadius': {
        (element as HTMLElement).style.borderRadius = newValue;
        (element as HTMLElement).style.setProperty('--custom-corner-radius', newValue);
        (element as HTMLElement).classList.add('editor-corner-radius-class');
        break;
      }
      case 'imageOverlay':
        (element as HTMLElement).parentElement?.style.setProperty(
          '--custom-image-overlay-color',
          newValue,
        );
        if (
          !(element as HTMLElement).parentElement?.classList.contains(
            'editor-image-overlay-wrapper-class',
          )
        ) {
          (element as HTMLElement).parentElement?.classList.add(
            'editor-image-overlay-wrapper-class',
          );
        }
        if (
          !(element as HTMLElement).nextElementSibling?.classList.contains(
            'editor-image-overlay-class',
          )
        ) {
          (element as HTMLElement).nextElementSibling?.classList.add('editor-image-overlay-class');
        }
        break;
      case 'overlay': {
        (element as HTMLElement).parentElement?.style.setProperty(
          '--custom-image-overlay-color',
          newValue,
        );
        if (
          !(element as HTMLElement).parentElement?.classList.contains(
            'editor-image-overlay-wrapper-class',
          )
        ) {
          (element as HTMLElement).parentElement?.classList.add(
            'editor-image-overlay-wrapper-class',
          );
        }
        (element as HTMLElement).style.setProperty('--custom-overlay-color', newValue);
        (element as HTMLElement).classList.add('editor-overlay-class');
        break;
      }
      case 'backgroundOpacity': {
        (element as HTMLElement).style.setProperty('--custom-background-opacity', newValue);
        (element as HTMLElement).classList.add('editor-background-opacity-class');
        break;
      }
      case 'videoCover': {
        (element as HTMLElement).style.setProperty('--custom-video-cover', `url('${newValue}')`);
        (element as HTMLElement).classList.add('editor-video-cover-class');
        break;
      }
      case 'htmlEmbed': {
        updateElementHtml(element as HTMLElement, newValue);
        break;
      }
      case 'videoOverlay': {
        (element as HTMLElement).style.setProperty('--custom-video-overlay', newValue);
        const childElement = element.querySelector('[class$="-iframe-overlay"]');
        if (childElement) {
          (childElement as HTMLElement).classList.add('editor-video-overlay-class');
        }

        break;
      }
      case 'videoOpacity': {
        (element as HTMLElement).style.setProperty('--custom-video-opacity', newValue);
        (element as HTMLElement).classList.add('editor-video-opacity-class');
        break;
      }
      case 'textColorOnHover': {
        (element as HTMLElement).style.setProperty('--custom-text-color-on-hover', newValue);
        (element as HTMLElement).classList.add('editor-text-color-on-hover-class');
        break;
      }
      case 'backgroundColorOnHover': {
        (element as HTMLElement).style.setProperty('--custom-background-color-on-hover', newValue);
        (element as HTMLElement).classList.add('editor-background-color-on-hover-class');
        break;
      }
      case 'strokeColorOnHover': {
        (element as HTMLElement).style.setProperty('--custom-stroke-color-on-hover', newValue);
        (element as HTMLElement).classList.add('editor-stroke-color-on-hover-class');
        break;
      }
      case 'iconBackgroundColor': {
        (element as HTMLElement).style.setProperty('--custom-icon-background-color', newValue);
        (element as HTMLElement).classList.add('editor-icon-background-color-class');
        break;
      }
      case 'iconBackgroundColorOnHover': {
        (element as HTMLElement).style.setProperty(
          '--custom-icon-background-color-on-hover',
          newValue,
        );
        (element as HTMLElement).classList.add('editor-icon-background-color-on-hover-class');
        break;
      }
      case 'iconColor': {
        (element as HTMLElement).style.setProperty('--custom-icon-color', newValue);
        (element as HTMLElement).classList.add('editor-icon-color-class');
        break;
      }
      case 'iconColorOnHover': {
        (element as HTMLElement).style.setProperty('--custom-icon-color-on-hover', newValue);
        (element as HTMLElement).classList.add('editor-icon-color-on-hover-class');
        break;
      }
      case 'link': {
        (element as HTMLElement).setAttribute('href', newValue || '');
        break;
      }
      case 'requireLeadForm': {
        if (newValue) {
          (element as HTMLElement).setAttribute('data-require-lead-form', 'true');
        } else {
          (element as HTMLElement).removeAttribute('data-require-lead-form');
        }
        break;
      }
      case 'visible': {
        if (option.value) {
          (element as HTMLElement).removeAttribute('data-favorite-hidden');
        } else {
          (element as HTMLElement).setAttribute('data-favorite-hidden', 'true');
        }
        break;
      }
      case 'autoplay': {
        if (newValue) {
          (element as HTMLElement).setAttribute('data-autoplay', 'true');
        } else {
          (element as HTMLElement).removeAttribute('data-autoplay');
        }
        break;
      }
      case 'subtitles': {
        if (newValue) {
          (element as HTMLElement).setAttribute('data-subtitles', 'true');
        } else {
          (element as HTMLElement).removeAttribute('data-subtitles');
        }
        break;
      }
      case 'value': {
        if (option.name === 'BackgroundImageComponent') {
          (element as HTMLElement).style.backgroundImage = `url('${newValue}')`;
        }
        if (option.name === 'IconsComponent') {
          (element as HTMLImageElement).src = newValue;
        }
        break;
      }
    }
  });
};

export function replaceMediaQueries(html: string): string {
  const replacements: { [key: string]: string } = {
    '1919px': '2057px',
    '1439px': '1577px',
    '1023px': '1161px',
    '639px': '777px',
  };

  Object.keys(replacements).forEach((oldQuery) => {
    const newQuery = replacements[oldQuery];
    html = html.replace(new RegExp(oldQuery, 'g'), newQuery);
  });
  return html;
}

export function replaceMediaQueriesBack(html: string): string {
  const replacements: { [key: string]: string } = {
    '2057px': '1919px',
    '1577px': '1439px',
    '1161px': '1023px',
    '777px': '639px',
  };

  Object.keys(replacements).forEach((oldQuery) => {
    const newQuery = replacements[oldQuery];
    html = html.replace(new RegExp(oldQuery, 'g'), newQuery);
  });
  return html;
}

export const getInitAssetTitle = (type: string, creativesList: CreativeAdItem[]) => {
  const filteredCreativesList = creativesList.filter(
    (creative) => creative.type.id.toString() === type.toString(),
  );
  const number = filteredCreativesList.length + 1;
  if (type === AdFormats.Carousel) {
    return `Carousel ${number}`;
  }
  if (type === AdFormats.Video) {
    return `Video ${number}`;
  }
  return `Single Image ${number}`;
};

export const addCardsStyles = (html: string, cards: CardType[]) => {
  // Create a temporary DOM element to parse and manipulate the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all elements with data-card-number attribute
  const cardElements = doc.querySelectorAll('[data-card-number]');

  console.log('cardElements', cardElements);
  console.log('cards', cards);
  cardElements.forEach((element) => {
    const cardNumber = element.getAttribute('data-card-number');
    if (cardNumber) {
      const cardIndex = parseInt(cardNumber, 10);

      console.log('cardIndex', cardIndex);
      // Check if the card exists in the cards array
      if (cards[cardIndex]) {
        // Add 'display: flex' to the current card element
        (element as HTMLElement).style.display = 'flex';
        (element as HTMLElement).setAttribute('data-hidden', 'false');
      } else {
        // Add 'display: none' to elements with data-card-number that don't have a corresponding card
        (element as HTMLElement).style.display = 'none';
        (element as HTMLElement).setAttribute('data-hidden', 'true');
      }
    }
  });

  // Return the modified HTML
  return doc.documentElement.outerHTML;
};

// Alternative implementation using regex if DOM manipulation is not available
export const addCardsStylesRegex = (html: string, cards: CardType[]) => {
  // Find all elements with data-card-number attribute using regex
  const cardNumberRegex = /<([^>]+)data-card-number="(\d+)"([^>]*)>/g;

  return html.replace(cardNumberRegex, (match, beforeAttr, cardNumber, afterAttr) => {
    const cardIndex = parseInt(cardNumber, 10);

    // Check if the card exists in the cards array
    if (cards[cardIndex]) {
      // Add 'display: flex' style
      if (beforeAttr.includes('style=')) {
        // If style attribute already exists, add display: flex to it
        return match.replace(/style="([^"]*)"/, (_styleMatch, styleValue) => {
          const newStyle = styleValue.includes('display:')
            ? styleValue.replace(/display:\s*[^;]+;?/, 'display: flex;')
            : styleValue + '; display: flex;';
          return `style="${newStyle}"`;
        });
      } else {
        // Add new style attribute with display: flex
        return `<${beforeAttr}style="display: flex;" data-card-number="${cardNumber}"${afterAttr}>`;
      }
    } else {
      // Add 'display: none' style
      if (beforeAttr.includes('style=')) {
        // If style attribute already exists, add display: none to it
        return match.replace(/style="([^"]*)"/, (_styleMatch, styleValue) => {
          const newStyle = styleValue.includes('display:')
            ? styleValue.replace(/display:\s*[^;]+;?/, 'display: none;')
            : styleValue + '; display: none;';
          return `style="${newStyle}"`;
        });
      } else {
        // Add new style attribute with display: none
        return `<${beforeAttr}style="display: none;" data-card-number="${cardNumber}"${afterAttr}>`;
      }
    }
  });
};
